import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Loader2, Handshake, Building2, Users, Globe, Award, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const partnerTypes = [
  "Hospital / Healthcare Facility",
  "Travel Agency",
  "Insurance Company",
  "Medical Equipment Supplier",
  "Pharmaceutical Company",
  "Wellness / Spa Resort",
  "Other"
];

const benefits = [
  { icon: Globe, title: "Global Patient Network", desc: "Access our network of patients from 50+ countries seeking quality healthcare in India." },
  { icon: Users, title: "Dedicated Support", desc: "Get a dedicated partnership manager to help coordinate patient referrals and care." },
  { icon: Award, title: "Quality Assurance", desc: "We maintain strict quality standards ensuring only the best facilities for our patients." },
  { icon: Building2, title: "Marketing Support", desc: "Benefit from our marketing efforts that drive qualified patient inquiries to partners." },
];

export default function PartnerWithUsPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    company_type: "",
    website: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.phone || !form.company_type || !form.message) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/partner-inquiry`, {
        ...form,
        source_page: window.location.href
      });
      setSuccess(true);
    } catch (err) {
      alert("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partner-with-us-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-orange-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-orange-600/10 text-orange-600 border-orange-600/20 mb-4 font-body text-xs uppercase tracking-wider">
              Partnership Opportunities
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-6">
              Partner With <span className="text-orange-600">Paxgrow</span>
            </h1>
            <p className="text-lg text-slate-600">
              Join India's fastest growing medical tourism facilitator. We're looking for hospitals, travel agencies, and healthcare providers who share our vision of accessible quality healthcare.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-4">Why Partner With Us?</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              We provide comprehensive support to help you grow your international patient base
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <Card key={i} className="bg-white rounded-2xl border border-slate-100 p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-4">
                  <b.icon className="h-7 w-7 text-orange-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                <p className="text-slate-500 text-sm">{b.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Form Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white rounded-2xl border border-slate-100 p-8 md:p-12">
            {success ? (
              <div className="py-12 text-center">
                <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">Partnership Inquiry Submitted!</h2>
                <p className="text-slate-500 mb-8">
                  Thank you for your interest in partnering with Paxgrow. Our partnership team will review your application and contact you within 2-3 business days.
                </p>
                <Button 
                  onClick={() => { setSuccess(false); setForm({ company_name: "", contact_name: "", email: "", phone: "", company_type: "", website: "", message: "" }); }}
                  className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8"
                >
                  Submit Another Inquiry
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <Handshake className="h-12 w-12 text-orange-600 mx-auto mb-4" />
                  <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">Partnership Inquiry Form</h2>
                  <p className="text-slate-500">Fill out the form below and our team will get back to you</p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm text-slate-600">Company/Organization Name *</Label>
                      <Input 
                        value={form.company_name}
                        onChange={e => setForm({...form, company_name: e.target.value})}
                        placeholder="Your company name"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Contact Person Name *</Label>
                      <Input 
                        value={form.contact_name}
                        onChange={e => setForm({...form, contact_name: e.target.value})}
                        placeholder="Your full name"
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm text-slate-600">Email Address *</Label>
                      <Input 
                        type="email"
                        value={form.email}
                        onChange={e => setForm({...form, email: e.target.value})}
                        placeholder="your@company.com"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Phone Number *</Label>
                      <Input 
                        value={form.phone}
                        onChange={e => setForm({...form, phone: e.target.value})}
                        placeholder="+1 234 567 8900"
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm text-slate-600">Type of Organization *</Label>
                      <Select value={form.company_type} onValueChange={v => setForm({...form, company_type: v})}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {partnerTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Website (Optional)</Label>
                      <Input 
                        value={form.website}
                        onChange={e => setForm({...form, website: e.target.value})}
                        placeholder="https://www.yourcompany.com"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600">Tell us about your organization and partnership interest *</Label>
                    <Textarea 
                      value={form.message}
                      onChange={e => setForm({...form, message: e.target.value})}
                      placeholder="Describe your organization, services, and how you'd like to partner with us..."
                      rows={5}
                      required
                      className="mt-1"
                    />
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-full py-6 font-semibold text-lg"
                  >
                    {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Submitting...</> : <>Submit Partnership Inquiry <ArrowRight className="ml-2 h-5 w-5" /></>}
                  </Button>
                </form>
              </>
            )}
          </Card>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: "50+", label: "Partner Hospitals" },
              { value: "15+", label: "Countries Served" },
              { value: "5000+", label: "Patients Facilitated" },
              { value: "8+", label: "Years Experience" },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-4xl font-bold mb-2">{s.value}</div>
                <div className="text-orange-100 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
